<template>
  <div class="main-block">
    <!-- Top Header (Fixed) -->
    <div
      class="header d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center bg-white p-3 shadow">
      <div class="mb-3 mb-xl-0">
        <h6 class="text-muted fs-6 fs-xl-5">{{ formattedDate }}</h6>
        <p class="fs-4 fs-xl-3">{{ $t('title.hello') }}, <span class="fw-bold">{{ user.full_name }}</span></p>
      </div>
    </div>

    <!-- Scrollable Content -->
    <div class="content-container">
      <div class="row mt-2">
        <!-- Left Panel: Insights -->
        <div class="col-xl-6">
          <div class="card p-3 insights-box">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">{{ $t('title.myInsights') }}</h5>
              <button @click="redirectToAddTask" class="btn btn-primary btn-sm">
                <i class="bi bi-plus"> {{ $t('title.addInsights') }}</i>
              </button>
            </div>
            <ul class="list-group insights-list d-flex flex-column gap-2 mt-2">
              <li v-for="(task, index) in insights" :key="task.id"
                class="list-group-item list-group-item-action list-group-item-light d-flex align-items-center">

                <!-- Text and Button Container -->
                <div class="d-flex justify-content-between w-100 align-items-center">
                  <span :class="{ 'truncated-text': !expanded[index] }" class="flex-grow-1">
                    {{ task.description }}
                  </span>
                  <button @click="toggleExpand(index)" class="btn btn-sm btn-outline-secondary ms-2">
                    <i :class="expanded[index] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                  </button>
                </div>

              </li>
            </ul>
          </div>
        </div>



        <!-- Middle Panel: Integration -->
        <div class="col-xl-3 mt-3 mt-xl-0">
          <div class="card p-3 integration-box">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">{{ $t('title.integration') }}</h5>
              <button @click="redirectToAddIntegration" class="btn btn-primary btn-sm">
                <i class="bi bi-plus">{{ $t('title.addIntegration') }}</i>
              </button>
            </div>
            <div class="integration-list d-flex flex-column gap-2 mt-2">
              <div v-for="integration in integrations" :key="integration.id"
                class="project-card d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center truncated-text" @click="navigateToService(integration)"
                  style="cursor: pointer;" :title="integration.name">
                  <img :src="logos[integration.services]" alt="Service Logo" class="service-logo me-2" />
                  <span class="m-1">{{ integration.name }}</span>
                </div>
                <span v-if="integration.sync_status === 'completed'" class="status-indicator bg-success m-2"
                  :title="$t('title.done')">
                </span>
                <span v-else class="status-indicator bg-danger m-2" :title="$t('title.failed')">
                </span>

              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel: System Users -->
        <div class="col-xl-3 mt-3 mt-xl-0">
          <div class="card p-3 system-user-box">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">{{ $t('title.systemUsers') }}</h5>
              <button @click="redirectToAddUser" class="btn btn-primary btn-sm">
                <i class="bi bi-plus">{{ $t('title.addUser') }}</i>
              </button>
            </div>
            <ul class="list-group system-user-list d-flex flex-column gap-2 mt-2">
              <li v-for="user in systemUsers" :key="user.id" class="list-group-item list-group-item-light"
                style="cursor: pointer;" @click="redirectToUser(user.id)"
                :title="`${$t('title.promptsSent')}: ${user.prompts_sent} | ${$t('title.insightsDelivered')}: ${user.insights_delivered}`"
                data-bs-toggle="tooltip">
                {{ user.full_name }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Filter Dropdown -->
      <div class="d-flex justify-content-start p-3">
        <select v-model="selectedTimeFilter" @change="fetchAllData" class="form-select w-auto">
          <option value="last_7_days">{{ $t('title.last7Days') }}</option>
          <option value="last_30_days">{{ $t('title.last30Days') }}</option>
        </select>
      </div>
      <!-- Row 2: Insights Charts -->
      <div class="row mt-3 mt-xl-0">
        <div class="col-xl-4">
          <div class="card p-3 text-center">
            <h6>{{ chartTitle }}</h6>
            <apexchart :type="chartType" height="250" :options="chartOptions" :series="chartData"></apexchart>
          </div>
        </div>
        <div class="col-xl-4 mt-3 mt-xl-0">
          <div class="card p-3 text-center">
            <h6>{{ $t('title.azureStorageOverTime') }}</h6>
            <apexchart type="line" height="250" :options="azureChartOptions" :series="graphData3"></apexchart>
          </div>
        </div>
        <div class="col-xl-4 mt-3 mt-xl-0">
          <div class="card p-3 text-center">
            <h6>{{ $t('title.rakoonPrompts') }}</h6>
            <apexchart type="bar" height="250" :options="rakoonChartOptions" :series="graphDataRakoon"></apexchart>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { request } from "@/Util/Request";
import VueApexCharts from "vue-apexcharts";
import moment from "moment";
import { onMounted } from "vue";
import { Tooltip } from "bootstrap";

export default {
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      expanded: {},
      insights: [],
      integrations: [],
      systemUsers: [],
      selectedTimeFilter: "last_7_days",
      isLoading: false,
      isLoadingAzurStorage: false,
      isLoadingRakoon: false,

      logos: {
        "G-suit": require("@/assets/google.png"),
        "Salesforce": require("@/assets/salesforce.png"),
        "ZOHO": require("@/assets/zoho.png"),
        "Monday": require("@/assets/monday.png"),
        "Microsoft 365": require("@/assets/office365.png"),
        "SAP": require("@/assets/sap.png"),
      },

      // Insights Distribution Chart
      chartOptions: {
        chart: { type: "bar", toolbar: { show: false } },
        xaxis: { categories: [] },
        colors: ["#007bff"],
      },
      chartData: [],
      chartTitle: this.$t('title.insightsOverTime'),
      chartType: "bar",

      // Azure Storage Chart
      azureChartOptions: {
        chart: { type: "line", toolbar: { show: false } },
        xaxis: { categories: [] },
        colors: ["#28a745"],
      },
      graphData3: [{ name: "Storage", data: [] }],

      // Rakoon Prompts Chart
      rakoonChartOptions: {
        chart: { type: "bar", toolbar: { show: false } },
        xaxis: { categories: [] },
        colors: ["#007bff"],
      },
      graphDataRakoon: [{ name: "Prompts", data: [] }],
    };
  },
  computed: {
    ...mapState(["user"]),
    formattedDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    },
  },
  async mounted() {
    await this.fetchInsights();
    this.fetchIntegrations();
    this.fetchData();
    this.fetchazurstorage();
    this.fetchRakoonPrompt();
    this.fetchSystemUsers();
  },
  methods: {
    toggleExpand(index) {
      this.$set(this.expanded, index, !this.expanded[index]);
    },
    async fetchSystemUsers() {
      try {
        const response = await request({
          method: 'get',
          url: '/admin/system-users-statistic',
        });
        this.systemUsers = response;
      } catch (error) {
        console.error("Error fetching system users:", error);
      }
    },
    navigateToService(item) {
      if (item.services === "G-suit") {
        this.$router.push({ name: "G-suitUsers", params: { id: item.id } });
      } else if (item.services === "Microsoft 365") {
        this.$router.push({ name: "Office365Users", params: { id: item.id } });
      } else if (item.services === "Salesforce") {
        if (item.data.product_service === "CRM") {
          this.$router.push({ name: "SalesforceCrmUsers", params: { id: item.id } });
        } else if (item.data.product_service === "Education Cloud") {
          this.$router.push({ name: "SalesforceEducationCloudUsers", params: { id: item.id } });
        } else if (item.data.product_service === "Analytics") {
          this.$router.push({ name: "SalesforceAnalyticsUsers", params: { id: item.id } });
        } else if (item.data.product_service === "Net Zero Cloud") {
          this.$router.push({ name: "SalesforceNetZeroCloudUsers", params: { id: item.id } });
        } else if (item.data.product_service === "Financial Services Cloud") {
          this.$router.push({ name: "SalesforceFinancialServicesCloudUsers", params: { id: item.id } });
        } else if (item.data.product_service === "Communications Cloud") {
          this.$router.push({ name: "SalesforceCommunicationsCloudUsers", params: { id: item.id } });
        }
      } else if (item.services === "Monday") {
        this.$router.push({ name: "MondayCRMContacts", params: { id: item.id } });
      } else if (item.services === "ZOHO") {
        this.$router.push({ name: "ZohoCrmUsers", params: { id: item.id } });
      } else {
        this.showToast(this.$t("title.error"), this.$t("title.serviceNotSupported"));
      }
    },
    redirectToChatAI(description) {
      const encodedDescription = encodeURIComponent(description);
      const baseUrl = window.location.origin; // Automatically detects local or production URL
      window.location.href = `${baseUrl}/chat-ai?description=${encodedDescription}`;
    },
    redirectToAddTask() {
      this.$router.push({ name: "AddScheduledTasks" });
    },
    redirectToAddIntegration() {
      this.$router.push({ name: "AddIntegrations" });
    },
    redirectToAddUser() {
      this.$router.push({ name: "AddUsers" });

    },
    redirectToUser(id) {
      this.$router.push({ name: 'ShowUser', params: { id: id, operation: 'show' } });
    },
    async fetchIntegrations() {
      try {
        const response = await request({ method: "get", url: "/admin/integrations" });
        this.integrations = response.data;
      } catch (error) {
        console.error("Error fetching integrations:", error);
      }
    },
    async fetchInsights() {
      try {
        const response = await request({ method: "get", url: "/admin/scheduled-task" });

        if (response.data) {
          this.insights = response.data.map((task) => ({
            id: task.id,
            description: task.description,
          }));
        } else {
          console.error("Error: response.data.data is undefined", response);
        }
      } catch (error) {
        console.error("Error fetching insights:", error);
      }
    },
    async fetchData() {
      this.isLoading = true;
      try {
        const { start_date, end_date } = this.getDateRange();

        const response = await request({
          method: "get",
          url: "/admin/system-scheduled-tasks",
          params: { start_date, end_date },
        });

        const data = response.scheduled_tasks || {};
        const typeData = Array.isArray(data.scheduled_tasks_over_time?.date) ? data.scheduled_tasks_over_time.date : [];
        const countData = Array.isArray(data.scheduled_tasks_over_time?.count) ? data.scheduled_tasks_over_time.count : [];

        this.chartOptions = {
          ...this.chartOptions,
          xaxis: { categories: typeData.map(String) } // Convert all to strings
        };

        this.chartData = [{ name: "Count", data: countData }];
      } catch (error) {
        console.error("Error fetching chart data:", error);
        this.chartData = [];
      } finally {
        this.isLoading = false;
      }
    },
    async fetchazurstorage() {
      this.isLoadingAzurStorage = true;
      try {
        const { start_date, end_date } = this.getDateRange();
        const response = await request({
          method: "get",
          url: "/admin/storage-data-over-time",
          params: { start_date, end_date },
        });

        const dates = Array.isArray(response?.date) ? [...response.date] : [];
        const totalSize = Array.isArray(response?.total_size) ? [...response.total_size] : [];

        this.graphData3 = [{ name: "Storage", data: totalSize }];

        this.azureChartOptions = {
          ...this.azureChartOptions,
          xaxis: { categories: dates },
        };
      } catch (error) {
        console.error("Error fetching Azure Storage:", error);
      } finally {
        this.isLoadingAzurStorage = false;
      }
    },

    async fetchRakoonPrompt() {
      this.isLoadingRakoon = true;
      try {
        const { start_date, end_date } = this.getDateRange();
        const response = await request({
          method: "get",
          url: "/admin/get-rakoon-prompt-data",
          params: { start_date, end_date },
        });

        // Extract data from API response
        const dates = response?.date?.map(String) || [];
        const userWiseData = response?.user_wise || {};

        if (!dates.length || !Object.keys(userWiseData).length) {
          console.error("Invalid data for Rakoon Prompt chart:", response);
          return;
        }

        // Define color palette (blue, green, yellow, and close shades)
        const colorPalette = ["#007bff", "#28a745", "#ffc107", "#17a2b8", "#6c757d"];

        // Extract unique users and assign colors
        const users = Array.from(new Set(Object.values(userWiseData).flatMap(Object.keys)));
        const userColors = users.reduce((acc, user, index) => {
          acc[user] = colorPalette[index % colorPalette.length]; // Cycle colors if more users than colors
          return acc;
        }, {});

        // Initialize series data
        const seriesData = {};
        users.forEach((user) => {
          seriesData[user] = Array(dates.length).fill(0);
        });

        // Populate actual user data counts
        dates.forEach((date, index) => {
          Object.entries(userWiseData[date] || {}).forEach(([user, count]) => {
            seriesData[user][index] = count;
          });
        });

        // Convert series data into ApexCharts format
        const graphDataRakoon = users.map((user) => ({
          name: user,
          data: seriesData[user],
        }));

        // Update the chart options dynamically
        this.graphDataRakoon = graphDataRakoon;
        this.rakoonChartOptions = {
          ...this.rakoonChartOptions,
          chart: {
            type: "bar",
            stacked: true,
            toolbar: { show: false },
          },
          xaxis: { categories: dates },
          colors: users.map((user) => userColors[user]),
          legend: { position: "top" },
          plotOptions: {
            bar: { horizontal: false },
          },
        };


      } catch (error) {
        console.error("Error fetching Rakoon Prompt:", error);
      } finally {
        this.isLoadingRakoon = false;
      }
    },

    // Function to determine the date range based on the selected filter
    getDateRange() {
      const days = this.selectedTimeFilter === "last_7_days" ? 7 : 30;
      const end_date = new Date().toISOString().split("T")[0]; // Today's date
      const start_date = new Date();
      start_date.setDate(start_date.getDate() - days);
      return {
        start_date: start_date.toISOString().split("T")[0],
        end_date,
      };
    },
    async fetchAllData() {
      await this.fetchData();
      await this.fetchazurstorage();
      await this.fetchRakoonPrompt();
    }
  },
};
</script>


<style scoped>
* {
  margin: 0;
  padding: 0;
}

.status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.insights-list .list-group-item,
.system-user-list .list-group-item {
  color: #000 !important;
  /* Black text */
}

.main-block {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.header {
  flex-shrink: 0;
}

.content-container {
  flex-grow: 1;
  overflow-y: auto;
  padding: 15px;
}

.project-card {
  background-color: #f8f9fa;
  padding: 10px 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.header {
  border-radius: 20PX;
}

.service-logo {
  width: 30px;
  height: 30px;
}

.insights-box,
.integration-box,
.system-user-box {
  height: 350px;
  overflow-y: auto;
}

.insights-list,
.integration-list,
.system-user-list {
  max-height: 350px;
  overflow-y: auto;
}

.system-user-list .list-group-item,
.insights-list .list-group-item {
  border: none !important;
  background-color: #f8f9fa !important;
  border-radius: 8px !important;
  padding: 10px 15px;
  transition: background-color 0.3s ease;
}

.system-user-list .list-group-item:hover,
.insights-list .list-group-item:hover {
  background-color: #e3f2fd !important;
}

.truncated-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.truncated-text.expanded {
  white-space: normal;
  overflow: visible;
}


.status-indicator {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  position: relative;
  cursor: pointer;
}

/* Tooltip effect */
.status-indicator:hover::after {
  content: attr(title);
  /* Shows the status text */
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 5px 8px;
  font-size: 12px;
  border-radius: 5px;
  white-space: nowrap;
  max-width: 150px;
  /* Prevents overflow */
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
  z-index: 999;
  /* Ensures it's on top */
}

/* Show tooltip smoothly */
.status-indicator:hover::after {
  opacity: 1;
}
</style>
